import {ChangeEvent, useLayoutEffect, useState} from 'react';
import {formatTransferAmountInput, normalizeTransferAmount} from './transferAmount';

const withoutGroups = (value: string) => value.replace(/[ \u00a0\u202f]/g, '');

const selectionOffset = (value: string, significantCount: number, afterGroup: boolean) => {
  let offset = 0;
  let count = 0;

  while (offset < value.length && count < significantCount) {
    if (value[offset] !== ' ') count += 1;
    offset += 1;
  }

  if (afterGroup && value[offset] === ' ') offset += 1;

  return offset;
};

// Одинаковое восстановление выделения для суммы и комиссии после группировки тысяч.
export const useTransferAmountInput = (value: string, onChange: (value: string) => void) => {
  const [selection, setSelection] = useState<{
    input: HTMLInputElement | HTMLTextAreaElement;
    start: number;
    end: number;
    direction: 'forward' | 'backward' | 'none' | null;
    afterGroup: boolean;
  } | null>(null);
  const formattedValue = formatTransferAmountInput(value);

  useLayoutEffect(() => {
    if (selection && document.activeElement === selection.input) {
      selection.input.setSelectionRange(
        selectionOffset(formattedValue, selection.start, selection.afterGroup),
        selectionOffset(formattedValue, selection.end, selection.afterGroup),
        selection.direction ?? undefined,
      );
    }
  }, [formattedValue, selection]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = event.target;
    const proposed = input.value;
    const normalized = normalizeTransferAmount(proposed);
    if (normalized === null) return;

    const start = input.selectionStart ?? proposed.length;
    const end = input.selectionEnd ?? start;
    // Нормализация начального разделителя добавляет ноль перед обоими концами выделения.
    const leadingZero = /^[.,]/.test(withoutGroups(proposed)) ? 1 : 0;
    setSelection({
      input,
      start: withoutGroups(proposed.slice(0, start)).length + (start > 0 ? leadingZero : 0),
      end: withoutGroups(proposed.slice(0, end)).length + (end > 0 ? leadingZero : 0),
      direction: input.selectionDirection,
      afterGroup:
        (event.nativeEvent as InputEvent).inputType === 'deleteContentForward' &&
        start === end &&
        formattedValue.length === proposed.length + 1 &&
        formattedValue[start] === ' ',
    });
    onChange(normalized);
  };

  return {value: formattedValue, onChange: handleChange};
};
